import React from "react";
import { AlertTriangle, Loader2, ShieldCheck } from "lucide-react";

import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { iconFor } from "@/components/Connectors/connectorIcons";
import axiosInstance from "@/utils/appsettings";
import type { Connector, CredentialField } from "@/hooks/useConnectors";

// Some services cannot be connected with an OAuth pop-up: a mail server the user runs, a corporate
// relay, a provider that only issues an API secret. This is the form for those.
//
// It renders entirely from the field schema the server sends, so adding Twilio or Outlook SMTP
// server-side needs no change here. Hardcoding one form per service is the mistake this avoids —
// it is the same mistake the old key-switch made on the connectors screen.

interface ConnectorCredentialsDialogProps {
  connector: Connector | null;
  onClose: () => void;
  /** Called once the credentials are stored, so the caller can refresh its view. */
  onSaved: () => void | Promise<void>;
}

const initialValues = (fields: CredentialField[]): Record<string, string> =>
  Object.fromEntries(fields.map((f) => [f.name, f.default || ""]));

const ConnectorCredentialsDialog: React.FC<ConnectorCredentialsDialogProps> = ({
  connector,
  onClose,
  onSaved,
}) => {
  const fields = React.useMemo(() => connector?.credentialFields ?? [], [connector]);
  const [values, setValues] = React.useState<Record<string, string>>(() => initialValues(fields));
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  // Reset per connector, not per render: reopening for a different service must not carry the
  // previous one's values, and a stored secret is never sent back to prefill
  React.useEffect(() => {
    setValues(initialValues(fields));
    setError(null);
  }, [connector?.key, fields]);

  if (!connector) return null;

  const Icon = iconFor(connector.icon);
  const missing = fields.filter((f) => f.required && !(values[f.name] || "").trim());

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await axiosInstance.post(`/connectors/${connector.key}/credentials`, { values });
      await onSaved();
      onClose();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: { error?: { message?: string } } } } })
        ?.response?.data?.detail?.error?.message;
      // The server checks the details actually work before storing them, so this message is
      // usually the real reason rather than a generic failure
      setError(detail || `Could not save your ${connector.name} details.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent
        className="flex max-h-[85vh] w-[calc(100vw-1.5rem)] max-w-md flex-col gap-0 overflow-hidden p-0 sm:w-auto"
        aria-describedby={undefined}
      >
        <header className="shrink-0 border-b border-gray-100 px-5 py-4 dark:border-white/10">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ink-blue-50 text-ink-blue-700 dark:bg-white/10 dark:text-white">
              <Icon className="h-7 w-7" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold text-ink-blue-900 dark:text-white">
                {connector.ctaLabel || `Set up ${connector.name}`}
              </h2>
              <p className="mt-0.5 text-xs leading-relaxed text-ink-blue-600 dark:text-gray-400">
                {connector.summary}
              </p>
            </div>
          </div>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {fields.map((field) => (
            <div key={field.name} className="space-y-1.5">
              <Label
                htmlFor={`cred-${field.name}`}
                className="text-[11px] font-medium uppercase tracking-wide text-ink-blue-400 dark:text-gray-500"
              >
                {field.label}
                {!field.required && (
                  <span className="ml-1.5 normal-case tracking-normal text-ink-blue-400 dark:text-gray-500">
                    (optional)
                  </span>
                )}
              </Label>

              {field.kind === "select" ? (
                <select
                  id={`cred-${field.name}`}
                  value={values[field.name] ?? ""}
                  disabled={saving}
                  onChange={(e) => setValues((v) => ({ ...v, [field.name]: e.target.value }))}
                  className="h-9 w-full rounded-md border border-gray-200 bg-white px-2.5 text-xs text-ink-blue-900 disabled:opacity-60 dark:border-white/10 dark:bg-white/5 dark:text-white"
                >
                  {field.options.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              ) : (
                <Input
                  id={`cred-${field.name}`}
                  type={field.kind === "password" ? "password" : field.kind === "number" ? "number" : "text"}
                  value={values[field.name] ?? ""}
                  placeholder={field.placeholder}
                  disabled={saving}
                  autoComplete={field.secret ? "new-password" : "off"}
                  onChange={(e) => setValues((v) => ({ ...v, [field.name]: e.target.value }))}
                  className="h-9 text-xs dark:border-white/10 dark:bg-white/5 dark:text-white"
                />
              )}

              {field.helpText && (
                <p className="text-[11px] leading-relaxed text-ink-blue-500 dark:text-gray-400">
                  {field.helpText}
                </p>
              )}
            </div>
          ))}

          <p className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50/70 px-2.5 py-2 text-[11px] leading-relaxed text-ink-blue-600 dark:border-white/10 dark:bg-white/5 dark:text-gray-400">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <span>
              Stored encrypted, and checked before saving so you find out here if something is
              wrong. The apps you build never see these details.
            </span>
          </p>

          {error && (
            <p className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-2 text-[11px] leading-relaxed text-red-800 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </p>
          )}
        </div>

        <footer className="flex shrink-0 flex-col-reverse items-stretch gap-2 border-t border-gray-100 bg-gray-50/80 px-5 py-3 sm:flex-row sm:items-center sm:justify-end dark:border-white/10 dark:bg-white/5">
          <Button variant="outline" size="sm" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={saving || missing.length > 0}
            className="gap-1.5 bg-ink-blue-900 text-white hover:bg-ink-blue-800 dark:bg-white dark:text-ink-blue-900 dark:hover:bg-gray-200"
            onClick={() => void save()}
          >
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {saving ? "Checking…" : "Save and connect"}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
};

export default ConnectorCredentialsDialog;
